import React from 'react';
import Alert from '@uber/base-icons/alert';
import Copy from '@uber/base-icons/copy';
import { styled } from 'baseui';
import { useSnackbar } from 'baseui/snackbar';
import { KIND as TAG_KIND, Tag } from 'baseui/tag';
import { colorWithOpacity } from '../../../common/utils/color-with-opacity';
import type { AnnotationInfoT } from '../annotations/types';
import { LONG_TOAST_DURATION, SHORT_TOAST_DURATION } from '../const';
import { COPY_ICON_SIZE } from './constants';
const Container = styled('div', ({
  $theme
}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: $theme.sizing.scale300,
  flexWrap: 'wrap'
}));
const tagOverrides = {
  Root: {
    style: ({
      $theme
    }) => ({
      margin: 0,
      padding: `${$theme.sizing.scale100} ${$theme.sizing.scale200}`,
      borderRadius: $theme.borders.radius200
    })
  },
  Text: {
    style: ({
      $theme
    }) => ({
      ...$theme.typography.LabelXSmall,
      gap: $theme.sizing.scale100,
      display: 'inline-block'
    })
  }
};
const idTagOverrides = {
  ...tagOverrides,
  Text: {
    style: ({
      $theme
    }) => ({
      ...tagOverrides.Text.style({
        $theme
      }),
      display: 'inline-flex',
      alignItems: 'center'
    })
  }
};
const truncateTagOverrides = {
  ...tagOverrides,
  Text: {
    style: ({
      $theme
    }) => ({
      ...tagOverrides.Text.style({
        $theme
      }),
      maxWidth: 'none'
    })
  }
};
const getAttributeTagOverrides = (color?: string, shouldTruncate?: boolean) => {
  const baseOverrides = shouldTruncate ? truncateTagOverrides : tagOverrides;
  if (!color) {
    return baseOverrides;
  }
  return {
    ...baseOverrides,
    Root: {
      props: {
        style: {
          backgroundColor: colorWithOpacity(color, 0.2)
        }
      },
      style: ({
        $theme
      }) => ({
        ...baseOverrides.Root.style({
          $theme
        }),
        backgroundColor: colorWithOpacity(color, 0.2)
      })
    },
    Text: {
      props: {
        style: {
          color
        }
      },
      style: ({
        $theme
      }) => ({
        ...baseOverrides.Text.style({
          $theme
        }),
        color
      })
    }
  };
};
type PropsT = {
  annotation: AnnotationInfoT;
};
export function AnnotationSubContent({
  annotation
}: PropsT) {
  const {
    id,
    attributes
  } = annotation;
  const {
    enqueue
  } = useSnackbar();
  const handleCopyId = async (e: Event) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      enqueue({
        message: 'Annotation ID copied to clipboard.',
        startEnhancer: () => <Copy title="Copied" />
      }, SHORT_TOAST_DURATION);
    } catch (error) {
      console.warn('Could not copy annotation ID.', error);
      enqueue({
        message: 'Could not copy annotation ID.',
        startEnhancer: () => <Alert title="Error" />
      }, LONG_TOAST_DURATION);
    }
  };
  return <Container>
      <Tag kind={TAG_KIND.neutral} closeable={false} overrides={idTagOverrides} onClick={handleCopyId}>
        <Copy size={COPY_ICON_SIZE} /> ID
      </Tag>
      {attributes.map((attribute, index) => {
      const shouldTruncate = attribute.hasOwnProperty('truncate') && attribute.truncate === false;
      return <Tag key={`${attribute.label}-${index}`} kind={attribute.kind} closeable={false} overrides={getAttributeTagOverrides(attribute.color, shouldTruncate)}>
            {attribute.label}
          </Tag>;
    })}
    </Container>;
}